import React, { useState } from "react";
import { Box, Chip, Collapse, IconButton, Tooltip } from "@mui/material";
import { CardContentWrapper } from "./Card";
import { AppTypography } from "./Typography";
import AppButton from "./Button";
import {
  ContentCopy as ContentCopyIcon,
  Inventory2Outlined as PackageIcon,
  ExpandMore as ExpandMoreIcon,
  ConfirmationNumberOutlined as CouponIcon,
  AccountBalanceWalletOutlined as WalletIcon,
} from "@mui/icons-material";
import giftIconImg from "../../assets/images/ion_gift-outline.png";
import pointsIconImg from "../../assets/images/points-icon.png";
import locationIconImg from "../../assets/images/location.png";
import calendarIconImg from "../../assets/images/calender.png";
import packIconImg from "../../assets/images/pack.png";
import {
  getShipmentStatus,
  getStatusChipProps,
} from "../../models/ShipmentStatus.js";
import { useStoreInfo } from "../../context/StoreInfoContext.js";

// Helper to check onesize SKUs (case-insensitive)
const isOnesizeSku = (sku) => {
  const size = sku?.toLowerCase().trim();
  return size === "onesize" || size === "one size" || size === "one-size";
};

export const OrderCardContentWrapper = ({
  order,
  theme,
  addressSummary,
  products,
}) => {
  const { storeInfo } = useStoreInfo();
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const shipment = order.shipment;
  const orderDate = new Date(shipment.created_date).toLocaleDateString(
    "en-US",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  );

  const shipmentStatus = getShipmentStatus(shipment.status);

  const copyAWBToClipboard = async (awb) => {
    try {
      await navigator.clipboard.writeText(awb);
    } catch (err) {
      console.error("Failed to copy AWB: ", err);
    }
  };

  const currencySymbol =
    String.fromCodePoint(storeInfo?.store?.currency_symbol || 0x20b9) || "₹";

  // Calculate totals for Payment Breakdown
  let subtotal = 0;
  const productBreakdown = products
    .map((product) => {
      const variants = product?.variants || [];
      const totalQty = variants.reduce((sum, v) => sum + (v.quantity || 0), 0);
      const lineTotal = variants.reduce(
        (sum, v) => sum + (v.quantity || 0) * (Number(v?.purchase_price) || 0),
        0,
      );
      subtotal += lineTotal;
      return { name: product.name, qty: totalQty, amount: lineTotal };
    })
    .filter((item) => item.qty > 0);

  const pointsUsed =
    order.points_redeemed ?? order.points_used ?? order.points ?? 0;
  const pointsValue = Number(order.points_value ?? pointsUsed) || 0;
  const couponDiscount = Number(order.coupon_discount) || 0;
  const totalAmount =
    order.amount != null
      ? Number(order.amount)
      : Math.max(0, subtotal - pointsValue - couponDiscount);

  const hasTrackingInfo =
    (shipmentStatus === "SHIPPED" ||
      shipmentStatus === "OUT FOR DELIVERY" ||
      shipmentStatus === "DELIVERED") &&
    shipment.awb_track_url &&
    shipment.awb_track_url !== "";

  const contentPadding = { xs: 2, sm: 3 };
  const statusChipProps = shipmentStatus
    ? getStatusChipProps(shipmentStatus)
    : null;

  return (
    <CardContentWrapper
      sx={{
        p: 0,
        overflow: "hidden",
        boxShadow:
          "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px;",
        "&:last-child": {
          pb: 1,
        },
      }}
    >
      {/* Order Header - full width, background spans edge to edge */}
      <Box
        sx={{
          backgroundColor: "#F9FAFB",
          px: contentPadding,
          py: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "#E0E7FF",
            }}
          >
            <PackageIcon
              sx={{
                fontSize: "1.25rem",
                color: "#4F39F6",
              }}
            />
          </Box>
          <Box>
            <AppTypography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                color: "#101828",
              }}
            >
              Order #{shipment.id}
            </AppTypography>
            <AppTypography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#6A7282",
              }}
            >
              Placed on {orderDate}
            </AppTypography>
          </Box>
        </Box>
        {shipmentStatus && statusChipProps && (
          <Chip
            label={shipmentStatus}
            color={statusChipProps.color}
            variant={statusChipProps.variant}
            sx={{
              fontSize: { xs: "0.65rem", sm: "0.7rem" },
              fontWeight: 400,
              height: { xs: "22px", sm: "24px" },
              borderRadius: "12px",
              ...statusChipProps.sx,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "rgba(0,0,0,0.08)",
        }}
      />

      {/* Content - padded, white background */}
      <Box
        sx={{
          px: contentPadding,
          py: 2,
          backgroundColor: "#ffffff",
        }}
      >
        {/* Product List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
          {products.map((product, index) => (
            <Box
              key={product.id || index}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: { xs: 0.75, sm: 1 },
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${theme.divider || "#e5e7eb"}`,
                  height: "78px",
                  width: "78px",
                  minWidth: "78px",
                  flexShrink: 0,
                }}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <AppTypography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#101828",
                    wordBreak: "break-word",
                  }}
                >
                  {product.name}
                </AppTypography>
                <Box>
                  {product?.variants?.map((variant, vIndex) => (
                    <AppTypography
                      key={vIndex}
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#6A7282",
                      }}
                    >
                      {isOnesizeSku(variant?.sku)
                        ? "Qty"
                        : variant.sku?.toUpperCase()}
                      {variant?.color && ` - ${variant.color}`}
                      {": "}
                      {variant.quantity}
                    </AppTypography>
                  ))}
                </Box>
                <AppTypography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#4F39F6",
                  }}
                >
                  {currencySymbol}
                  {(product?.variants || [])
                    .reduce(
                      (sum, v) =>
                        sum +
                        (v.quantity || 0) * (Number(v?.purchase_price) || 0),
                      0,
                    )
                    .toFixed(2)}
                </AppTypography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Action Buttons - View Details & Track Shipment only (no Download Invoice) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <AppButton
            variant="outlined"
            size="small"
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: detailsExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            }
            onClick={() => setDetailsExpanded(!detailsExpanded)}
            sx={{
              order: { xs: 2, sm: 1 },
              background: theme?.app_button_color || "#000000",
              height: "32px",
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "110px" },
              color: "white",
              padding: "6px 12px",
              borderRadius: "8px",
              marginTop: "0.7rem",
              textAlign: "center",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "12px",
              textTransform: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                background: theme?.app_button_secondary_color || "#000000",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                borderColor: "transparent",
              },
            }}
          >
            View Details
          </AppButton>
          {hasTrackingInfo && (
            <AppButton
              variant="outlined"
              size="small"
              onClick={() => window.open(shipment.awb_track_url, "_blank")}
              sx={{
                order: { xs: 1, sm: 2 },
                background: theme?.app_button_color || "#000000",
                height: "32px",
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "110px" },
                color: "white",
                padding: "6px 12px",
                borderRadius: "8px",
                marginTop: "0.7rem",
                textAlign: "center",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: "12px",
                textTransform: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: theme?.app_button_secondary_color || "#000000",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                  borderColor: "transparent",
                },
              }}
            >
              Track Shipment
            </AppButton>
          )}
        </Box>

        {/* Expandable Details - Tracking ID & Payment Breakdown */}
        <Collapse in={detailsExpanded}>
          <Box
            sx={{
              py: 2,
              mt: 2,
              px: contentPadding,
              borderRadius: "14px",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              "@media (min-width: 600px)": {
                paddingLeft: "0px",
                paddingRight: "0px",
              },
            }}
          >
            {/* Delivery and Tracking Information */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Box
                  component="img"
                  src={locationIconImg}
                  alt="Location"
                  sx={{
                    width: "1.1rem",
                    height: "1.1rem",
                    flexShrink: 0,
                    mt: 0.25,
                    objectFit: "contain",
                  }}
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <AppTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#99A1AF",
                    }}
                  >
                    Delivery Address
                  </AppTypography>
                  <AppTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#4A5565",
                      wordBreak: "break-word",
                    }}
                  >
                    {addressSummary || "—"}
                  </AppTypography>
                </Box>
              </Box>

              {shipmentStatus === "DELIVERED" && (
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Box
                    component="img"
                    src={calendarIconImg}
                    alt="Calendar"
                    sx={{
                      width: "1.1rem",
                      height: "1.1rem",
                      flexShrink: 0,
                      mt: 0.25,
                      objectFit: "contain",
                    }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <AppTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#99A1AF",
                      }}
                    >
                      Delivered on
                    </AppTypography>
                    <AppTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#4A5565",
                      }}
                    >
                      {shipment.delivery_date
                        ? new Date(shipment.delivery_date).toLocaleDateString(
                            "en-US",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : orderDate}
                    </AppTypography>
                  </Box>
                </Box>
              )}

              {shipment.awb && (
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Box
                    component="img"
                    src={packIconImg}
                    alt="Package"
                    sx={{
                      width: "1.1rem",
                      height: "1.1rem",
                      flexShrink: 0,
                      mt: 0.25,
                      objectFit: "contain",
                    }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <AppTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#99A1AF",
                      }}
                    >
                      Tracking Number
                    </AppTypography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <AppTypography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#4A5565",
                          wordBreak: "break-word",
                        }}
                      >
                        {shipment.awb}
                      </AppTypography>
                      <Tooltip title="Copy">
                        <IconButton
                          size="small"
                          onClick={() => copyAWBToClipboard(shipment.awb)}
                          sx={{
                            padding: "2px",
                            color: "#6B7280",
                            "&:hover": { color: theme?.app_button_color },
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Payment Breakdown - inside accordion */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: "12px",
                backgroundColor: "#F3F4F6",
                border: "1px solid #E5E7EB",
              }}
            >
              <AppTypography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#101828",
                  mb: 1.5,
                }}
              >
                Payment Breakdown
              </AppTypography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {productBreakdown.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        component="img"
                        src={giftIconImg}
                        alt="Gift"
                        sx={{
                          width: "1rem",
                          height: "1rem",
                          opacity: 0.8,
                        }}
                      />
                      <AppTypography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#364153",
                        }}
                      >
                        {item.name} (Qty: {item.qty})
                      </AppTypography>
                    </Box>
                    <AppTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#2A282E",
                      }}
                    >
                      {currencySymbol}
                      {item.amount.toFixed(2)}
                    </AppTypography>
                  </Box>
                ))}
                {pointsUsed > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        component="img"
                        src={pointsIconImg}
                        alt="Points"
                        sx={{
                          width: "1rem",
                          height: "1rem",
                          opacity: 0.9,
                        }}
                      />
                      <AppTypography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#364153",
                        }}
                      >
                        Points Used
                      </AppTypography>
                    </Box>
                    <AppTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#364153",
                      }}
                    >
                      {pointsUsed} pts
                    </AppTypography>
                  </Box>
                )}
                {(couponDiscount > 0 || order.coupon_code) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CouponIcon
                        sx={{
                          fontSize: "1rem",
                          color: "#f59e0b",
                          opacity: 0.9,
                        }}
                      />
                      <AppTypography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#364153",
                        }}
                      >
                        Coupons Applied
                      </AppTypography>
                    </Box>
                    <AppTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#364153",
                      }}
                    >
                      {order.coupon_code ||
                        `-${currencySymbol}${couponDiscount.toFixed(2)}`}
                    </AppTypography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <WalletIcon
                      sx={{
                        fontSize: "1rem",
                        color: "#10B981",
                        opacity: 0.9,
                      }}
                    />
                    <AppTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#364153",
                      }}
                    >
                      Paid Amount
                    </AppTypography>
                  </Box>
                  <AppTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#364153",
                    }}
                  >
                    {totalAmount <= 0
                      ? `${currencySymbol}0 (No payment required)`
                      : `${currencySymbol}${totalAmount.toFixed(2)}`}
                  </AppTypography>
                </Box>
              </Box>
              <Box
                sx={{
                  borderTop: `1px solid ${theme.divider || "#e5e7eb"}`,
                  mt: 1.5,
                  pt: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <AppTypography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "#101828",
                  }}
                >
                  Total Order Value
                </AppTypography>
                <AppTypography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "#101828",
                  }}
                >
                  {currencySymbol}
                  {subtotal.toFixed(2)}
                </AppTypography>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </CardContentWrapper>
  );
};
